// src/pages/admin/AssociateFee.jsx
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  Card,
  PaginatedTable,
  DataStateHandler,
  ConfirmModal,
  Modal,
  Button,
  CheckboxField,
  StickyHorizontalScroll,
  BulkEmailSummary,
  ColumnToggle,
  usePersistedState,
  useScrollRestore,
} from "@common";
import {
  calculateDdsDraw,
  payrollColumns,
  toSortableNumber,
  getComparableValue,
  getFilterLabel,
  compareValues,
  formatHeaderTotal,
  TOTALABLE_COLUMN_CONFIG,
  TOTAL_BADGE_COLORS,
  getDdsHistoryKey,
  INLINE_EDITABLE_FIELDS,
  INLINE_EDITABLE_MONTHLY_FIELDS,
  INLINE_REQUIRED_CREATE_FIELDS,
  INLINE_FIELD_LABELS,
  INLINE_ACTION_COLUMN_KEY,
  isGridTierDoctor,
  DEDUCTION_COLUMN_KEYS,
  buildAssociateFeeExportRows,
} from "@utils/associateFee";
import {
  toDraftValue,
  toNullableNumber,
  areNullableNumbersEqual,
  parseDraftNumber,
} from "@utils/inlineEditDraft";
import { useInlineEditStore } from "@hooks/useInlineEditStore";
import AssociateFeeHeaderMenu from "./AssociateFeeHeaderMenu";
import { InlineEditableCell, InlineEditActionCell } from "./AssociateFeeInlineCells";
import DdsComparisonModal from "./DdsComparisonModal";
import DdsCompletionFilterBar from "./DdsCompletionFilterBar";
import PractitionerModalWrapper from "./PractitionerModalWrapper";
import { useHeaderMenuStore } from "@hooks/useHeaderMenuStore";
import api from "@api/api";
import { adminEndpoints, clinicEndpoints } from "@api/endpoints";
import { buildUrlWithParams } from "@api/queryHelpers";
import {
  useAssociateFeeRowsMulti,
  useUpdatePractitionerByClinic,
  useCreateMonthlyCompensationForClinic,
  useSendPdfEmail,
  useBulkSendPdfEmail,
  useBulkEmailStatus,
  useDownloadPdf,
  useDownloadLabInvoiceZip,
  usePreviewPdf,
  useUpdateMonthlyCompensationForClinic,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { showSuccess, showError } from "@common";
import {
  isOrganizerOrAdmin,
  isObserver,
  isAreaManager,
  isPayroll,
  isFinanceCoordinator,
  isManagerOrAbove,
  canApproveCompensation,
  canEmailCompensationPdf,
  canDownloadCompensationPdf,
} from "@utils";
import { isApprovalApproved } from "@utils/associateFee";
import { getApiErrorMessage } from "@utils/apiErrorUtils";

const DDS_COMPLETION_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "not_completed", label: "Not Completed" },
  { value: "not_worked", label: "No Data" },
  { value: "data_mismatch", label: "Mismatch" },
  { value: "grid", label: "Grid" },
];

const shiftMonth = (monthValue, offset) => {
  if (!monthValue) return null;
  const [year, monthNumber] = String(monthValue).split("-").map(Number);
  if (!year || !monthNumber) return null;
  const date = new Date(Date.UTC(year, monthNumber - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const isRequestCanceled = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.message === "canceled";

// Hoisted so PaginatedTable / Pagination get a stable reference and don't
// re-render every parent commit just because of a new array literal.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

export default function AssociateFee({
  months,
  clinics,
  // Legacy single-value props (kept for back-compat with any other caller).
  month: legacyMonth,
  clinic: legacyClinic,
  clinicCodes = null,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
}) {
  // Normalize to arrays so the rest of the component can assume array shape.
  const selectedMonths = useMemo(() => {
    if (Array.isArray(months) && months.length) return months;
    if (legacyMonth) return [legacyMonth];
    return [];
  }, [months, legacyMonth]);
  const selectedClinics = useMemo(() => {
    if (Array.isArray(clinics) && clinics.length) return clinics;
    if (legacyClinic) return [legacyClinic];
    return [];
  }, [clinics, legacyClinic]);
  // Area-manager fallback: when no clinic is explicitly picked but an area
  // manager is, the caller passes that manager's clinics here instead.
  const selectedClinicCodes = useMemo(
    () => (Array.isArray(clinicCodes) ? clinicCodes.filter(Boolean) : []),
    [clinicCodes],
  );

  // Single-string `clinic` query param expected by the backend (comma-separated).
  // Falls back to the area-manager list so that case is filtered in SQL too —
  // it used to fetch every clinic and narrow in the browser. Empty means NOTHING
  // is selected, and the query below stays disabled rather than sending no
  // filter at all, which the backend reads as "every clinic".
  const clinicFilter = useMemo(
    () => (selectedClinics.length ? selectedClinics : selectedClinicCodes).join(","),
    [selectedClinics, selectedClinicCodes],
  );
  // Used by the few code paths that still need a single month value
  // (previous-month-draws lookup, in-place creation payloads, etc).
  // Falls back to the first selected month when multiple are picked.
  const month = selectedMonths[0] || "";
  const monthsKey = useMemo(() => selectedMonths.join(","), [selectedMonths]);
  const { user } = useAuth();
  const isAdminOrOrg = isOrganizerOrAdmin(user);
  const canApprove = canApproveCompensation(user);
  // Separate from `canApprove`: mailing a practitioner their statement is its
  // own authority (org/admin/AM/payroll), not a by-product of approving.
  const canSendEmail = canEmailCompensationPdf(user);
  const canDownloadPdf = canDownloadCompensationPdf(user);

  const [emailConfirmRow, setEmailConfirmRow] = useState(null);
  // { row, step } pending approval confirmation
  const [approvalConfirm, setApprovalConfirm] = useState(null);
  // { row, step } pending reset-request confirmation
  const [resetRequestConfirm, setResetRequestConfirm] = useState(null);
  // { row, decision: 'approve' | 'reject' } pending reset-resolution
  const [resetResolveConfirm, setResetResolveConfirm] = useState(null);
  // Row edits (inline cells + the practitioner modal). Backed by
  // `update_for_clinic`, whose write branch is can_write_clinic_data (1,2,3,4,9)
  // OR can_act_on_payroll (1,2,7,9) — Finance Coordinator (9) qualifies via both.
  // The 3-step approval workflow below stays role-named and is NOT widened.
  const canEditPractitioner =
    isManagerOrAbove(user) || isPayroll(user) || isFinanceCoordinator(user);
  const [previousMonthDraws, setPreviousMonthDraws] = useState({});
  const compareModalRef = useRef(null);
  const practitionerModalRef = useRef(null);

  // Bulk email state
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEligible, setBulkEligible] = useState([]);
  const [bulkAlreadySent, setBulkAlreadySent] = useState([]);
  const [bulkMissingEmail, setBulkMissingEmail] = useState([]);
  const [bulkNotApproved, setBulkNotApproved] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [columnFilters, setColumnFilters] = usePersistedState("associateFee:columnFilters", {});
  const [ddsCompletionFilter, setDdsCompletionFilter] = usePersistedState(
    "associateFee:ddsCompletionFilter",
    "all",
  );
  const inlineEditStore = useInlineEditStore();
  const completionFilterBarRef = useRef(null);
  const menuStore = useHeaderMenuStore();
  const [sortKey, setSortKey] = usePersistedState("associateFee:sortKey", null);
  const [sortDir, setSortDir] = usePersistedState("associateFee:sortDir", null);
  const [isSorting, startSortTransition] = useTransition();
  const [isFiltering, startFilterTransition] = useTransition();
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("associateFee_hiddenColumns");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Approval state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);

  // Deduction columns (purple) collapse into a single "Total Fee" column
  // by default. The Total Fee header has a +/- button that flips this.
  const [deductionsExpanded, setDeductionsExpanded] = usePersistedState(
    "associateFee:deductionsExpanded",
    false,
  );
  const toggleDeductions = useCallback(
    () => setDeductionsExpanded((v) => !v),
    [setDeductionsExpanded],
  );

  // Hydrate the header-menu store from persisted filters/sort on mount so
  // the column header menus reflect the restored selection (the menuStore
  // is a separate ref-based store; React state alone won't update it).
  useEffect(() => {
    Object.entries(columnFilters || {}).forEach(([key, val]) => {
      if (val) menuStore.setFilter(key, val);
    });
    if (sortKey) menuStore.setSort(sortKey, sortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryClient = useQueryClient();
  const updateMut = useUpdatePractitionerByClinic();
  const createMonthlyMut = useCreateMonthlyCompensationForClinic();
  const inlineMonthlyUpdateMut = useUpdateMonthlyCompensationForClinic();
  const sendEmailMut = useSendPdfEmail();
  const bulkEmailMut = useBulkSendPdfEmail();
  const downloadPdfMut = useDownloadPdf();
  const downloadLabInvoiceZipMut = useDownloadLabInvoiceZip();
  const previewPdfMut = usePreviewPdf();
  const approveMut = useUpdateMonthlyCompensationForClinic();

  const [previewPdf, setPreviewPdf] = useState(null); // { url, title }
  // Which row action is currently running: { id, action } | null. Row-scoped
  // because the mutations are shared across every row — a bare `isPending`
  // would spin all of them. Each statement PDF now waits on a NetSuite lookup
  // for its checks, so these take long enough to need the feedback.
  const [pdfBusy, setPdfBusy] = useState(null);

  // const [drWaelResult, setDrWaelResult] = useState(null);

  const { data, isLoading, error } = useAssociateFeeRowsMulti(
    {
      months: selectedMonths,
      practitioner_type: "dds",
      // Always include these specific HYG practitioners alongside DDS rows
      // (Sheila Holbein profile #201, Dan Porter profile #202).
      // NOTE: these are practitioner_profiles.id values, not Practitioner.id.
      include_profile_ids: "201,202",
      limit: 10000,
      offset: 0,
      ...(clinicFilter ? { clinic: clinicFilter } : {}),
    },
    { enabled: selectedMonths.length > 0 && !!clinicFilter },
  );

  useScrollRestore(`associateFee:scroll:${monthsKey}:${clinicFilter || ""}`, !isLoading);

  // Poll bulk email job status
  const { data: bulkJobStatus } = useBulkEmailStatus(bulkJobId);

  useEffect(() => {
    if (!bulkJobStatus || !bulkJobId) return;
    if (bulkJobStatus.status === "done") {
      setBulkSending(false);
      setBulkEmailOpen(false);
      setBulkJobId(null);
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
      const { sent, failed, results } = bulkJobStatus;
      if (failed === 0) {
        showSuccess(`All ${sent} emails sent successfully!`);
      } else {
        const failedNames = (results || [])
          .filter((r) => r.status === "failed")
          .map((r) => r.name || `ID ${r.id}`)
          .join(", ");
        showError(`${sent} sent, ${failed} failed (${failedNames}).`);
      }
    } else if (bulkJobStatus.status === "failed") {
      setBulkSending(false);
      setBulkEmailOpen(false);
      setBulkJobId(null);
      showError("Bulk email job failed. Please try again.");
    }
  }, [bulkJobStatus, bulkJobId, queryClient]);

  // ✅ reset to page 1 when months, clinic, or page size changes (matches PerformanceGlance behavior)
  useEffect(() => {
    onPageChange?.(1);
  }, [monthsKey, clinicFilter, clinicCodes, pageSize, onPageChange]);

  const limit = pageSize;
  const offset = (page - 1) * pageSize;

  const userClinics = useMemo(() => user?.clinic || [], [user?.clinic]);
  // Previous-month draw lookup only makes sense for a single selected month;
  // when multiple months are selected we skip it (the UI hint isn't worth
  // N parallel fetches per month).
  const previousMonth = useMemo(
    () => (selectedMonths.length === 1 ? shiftMonth(month, -1) : null),
    [selectedMonths.length, month],
  );
  const selectedClinicCodeSet = useMemo(
    () => new Set(selectedClinicCodes.map((code) => String(code).toUpperCase())),
    [selectedClinicCodes],
  );
  const hasSelectedClinicCodes = selectedClinicCodeSet.size > 0;
  const rows = useMemo(() => {
    const baseRows = data?.results ?? [];
    if (!hasSelectedClinicCodes) return baseRows;
    return baseRows.filter((row) =>
      selectedClinicCodeSet.has(String(row.clinic || "").toUpperCase()),
    );
  }, [data, hasSelectedClinicCodes, selectedClinicCodeSet]);
  const deferredColumnFilters = useDeferredValue(columnFilters);
  const deferredDdsCompletionFilter = useDeferredValue(ddsCompletionFilter);
  const isFilterSettling =
    deferredColumnFilters !== columnFilters || deferredDdsCompletionFilter !== ddsCompletionFilter;

  const fetchMonthlyCompensationRows = useCallback(async ({ clinicCode, filters, signal }) => {
    const url = buildUrlWithParams(
      clinicCode
        ? clinicEndpoints.GET_MONTHLY_COMPENSATION_BY_CLINIC(clinicCode)
        : adminEndpoints.GET_ALL_MONTHLY_COMPENSATIONS,
      {
        practitioner_type: "dds",
        limit: 10000,
        offset: 0,
        ...filters,
      },
      { clean: true },
    );
    const response = await api.get(url, { signal });
    return response.data?.results ?? [];
  }, []);

  useEffect(() => {
    if (!previousMonth) {
      setPreviousMonthDraws({});
      return undefined;
    }

    const controller = new AbortController();

    const loadPreviousMonthDraws = async () => {
      try {
        let previousRows = [];

        // Observer reads every clinic like admin.
        if (isAdminOrOrg || isObserver(user)) {
          previousRows = await fetchMonthlyCompensationRows({
            filters: {
              month: previousMonth,
              ...(clinicFilter ? { clinic: clinicFilter } : {}),
            },
            signal: controller.signal,
          });
        } else if (clinicFilter) {
          previousRows = await fetchMonthlyCompensationRows({
            clinicCode: clinicFilter,
            filters: { month: previousMonth },
            signal: controller.signal,
          });
        } else if (userClinics.length > 0) {
          const results = await Promise.all(
            userClinics.map((clinicCode) =>
              fetchMonthlyCompensationRows({
                clinicCode,
                filters: { month: previousMonth },
                signal: controller.signal,
              }),
            ),
          );
          previousRows = results.flat();
        }

        if (controller.signal.aborted) return;

        const lookup = {};
        previousRows
          .filter(
            (row) =>
              !hasSelectedClinicCodes ||
              selectedClinicCodeSet.has(String(row.clinic || "").toUpperCase()),
          )
          .forEach((row) => {
            lookup[getDdsHistoryKey(row)] = calculateDdsDraw(row);
          });
        setPreviousMonthDraws(lookup);
      } catch (error) {
        if (isRequestCanceled(error)) return;
        setPreviousMonthDraws({});
      }
    };

    loadPreviousMonthDraws();

    return () => controller.abort();
  }, [
    previousMonth,
    isAdminOrOrg,
    user,
    clinicFilter,
    userClinics,
    fetchMonthlyCompensationRows,
    hasSelectedClinicCodes,
    selectedClinicCodeSet,
  ]);

  const columnTotals = useMemo(() => {
    const totals = {};

    Object.keys(TOTALABLE_COLUMN_CONFIG).forEach((key) => {
      totals[key] = rows.reduce((sum, row) => {
        const value = getComparableValue(row, key);
        const numericValue = toSortableNumber(value);
        return sum + (numericValue ?? 0);
      }, 0);
    });

    return totals;
  }, [rows]);

  const rowsAfterColumnFilters = useMemo(
    () =>
      rows.filter((row) => {
        return Object.entries(deferredColumnFilters).every(([key, selected]) => {
          if (!selected) return true;
          return selected.has(getFilterLabel(row, key));
        });
      }),
    [rows, deferredColumnFilters],
  );

  const completionCounts = useMemo(
    () =>
      rowsAfterColumnFilters.reduce(
        (counts, row) => {
          const status = row.completion_status || "not_worked";
          counts.all += 1;
          counts[status] += 1;
          if (isGridTierDoctor(row)) {
            counts.grid += 1;
          }
          return counts;
        },
        {
          all: 0,
          completed: 0,
          not_completed: 0,
          not_worked: 0,
          data_mismatch: 0,
          grid: 0,
        },
      ),
    [rowsAfterColumnFilters],
  );

  const filteredRows = useMemo(
    () =>
      rowsAfterColumnFilters.filter((row) => {
        if (deferredDdsCompletionFilter === "all") return true;
        if (deferredDdsCompletionFilter === "grid") {
          return isGridTierDoctor(row);
        }
        return row.completion_status === deferredDdsCompletionFilter;
      }),
    [rowsAfterColumnFilters, deferredDdsCompletionFilter],
  );

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortDir) return [...filteredRows];

    const decorated = filteredRows.map((row) => ({
      row,
      val: getComparableValue(row, sortKey),
    }));

    decorated.sort((a, b) => {
      const result = compareValues(a.val, b.val, sortKey);
      return sortDir === "desc" ? -result : result;
    });

    return decorated.map((d) => d.row);
  }, [filteredRows, sortKey, sortDir]);

  const total = sortedRows.length;
  const pagedRows = useMemo(
    () => sortedRows.slice(offset, offset + limit),
    [sortedRows, offset, limit],
  );
  const activeFilterCount =
    Object.keys(columnFilters).length + (ddsCompletionFilter === "all" ? 0 : 1);

  const handleUpdateClick = useCallback(
    (row) =>
      practitionerModalRef.current?.open(row, {
        openInEdit: true,
        canEdit: canEditPractitioner,
      }),
    [canEditPractitioner],
  );

  const handlePractitionerNameClick = useCallback(
    (row) =>
      practitionerModalRef.current?.open(row, {
        openInEdit: false,
        canEdit: canEditPractitioner,
      }),
    [canEditPractitioner],
  );

  const handleCompareHistory = useCallback((row) => {
    compareModalRef.current?.open(row);
  }, []);

  const handleSavePractitioner = useCallback(
    async (original, payload) => {
      await updateMut.mutateAsync({
        clinicCode: original.clinic,
        id: original.id,
        data: payload,
        method: "patch",
      });
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
    },
    [updateMut, queryClient],
  );

  const getInlineRowKey = useCallback(
    (row) =>
      `${row.id ?? "new"}::${row.clinic ?? ""}::${row.practitioner_id ?? ""}::${row.month ?? ""}`,
    [],
  );

  const startInlineEdit = useCallback(
    (row) => {
      const isApprovalLocked = isApprovalApproved(row.admin_approval) && !isAdminOrOrg;
      if (!canEditPractitioner || isApprovalLocked || inlineEditStore.getSavingRowKey()) return;

      const initialDraft = INLINE_EDITABLE_FIELDS.reduce((draft, key) => {
        draft[key] = toDraftValue(row[key]);
        return draft;
      }, {});
      inlineEditStore.beginEdit(getInlineRowKey(row), initialDraft);
    },
    [canEditPractitioner, getInlineRowKey, inlineEditStore, isAdminOrOrg],
  );

  const cancelInlineEdit = useCallback(() => {
    if (inlineEditStore.getSavingRowKey()) return;
    inlineEditStore.cancelEdit();
  }, [inlineEditStore]);

  const updateInlineDraft = useCallback(
    (rowKey, field, value) => {
      inlineEditStore.updateDraft(rowKey, field, value);
    },
    [inlineEditStore],
  );

  const saveInlineEdit = useCallback(
    async (row) => {
      const rowKey = getInlineRowKey(row);
      const editingRowKey = inlineEditStore.getEditingRowKey();
      if (!editingRowKey || editingRowKey !== rowKey) return;
      if (inlineEditStore.getSavingRowKey()) return;

      const draft = inlineEditStore.getDraft(rowKey) ?? {};

      const parsedInsurance = parseDraftNumber(draft.insurance, {
        label: INLINE_FIELD_LABELS.insurance,
        min: 0,
        allowBlank: true,
      });
      if (parsedInsurance.error) {
        showError(parsedInsurance.error);
        return;
      }

      const practitionerPayload = {};
      if (!areNullableNumbersEqual(row.insurance, parsedInsurance.value)) {
        practitionerPayload.insurance = parsedInsurance.value;
      }

      const monthlyPayload = {};
      for (const field of INLINE_EDITABLE_MONTHLY_FIELDS) {
        const parsed = parseDraftNumber(draft[field], {
          label: INLINE_FIELD_LABELS[field],
          min: 0,
          allowBlank: true,
        });
        if (parsed.error) {
          showError(parsed.error);
          return;
        }

        if (parsed.value === null && INLINE_REQUIRED_CREATE_FIELDS.has(field) && row.id != null) {
          if (!areNullableNumbersEqual(row[field], parsed.value)) {
            showError(`${INLINE_FIELD_LABELS[field]} cannot be blank.`);
            return;
          }
        }

        if (!areNullableNumbersEqual(row[field], parsed.value)) {
          monthlyPayload[field] = parsed.value;
        }
      }

      const hasPractitionerChanges = Object.keys(practitionerPayload).length > 0;
      const hasMonthlyChanges = Object.keys(monthlyPayload).length > 0;
      if (!hasPractitionerChanges && !hasMonthlyChanges) {
        inlineEditStore.cancelEdit();
        return;
      }

      if (!inlineEditStore.startSaving(rowKey)) return;
      try {
        if (hasPractitionerChanges) {
          if (!row.clinic || !row.practitioner_id) {
            throw new Error("Missing clinic/practitioner for practitioner update.");
          }
          await updateMut.mutateAsync({
            clinicCode: row.clinic,
            id: row.practitioner_id,
            data: practitionerPayload,
            method: "patch",
          });
        }

        if (hasMonthlyChanges) {
          if (!row.clinic || !row.practitioner_id) {
            throw new Error("Missing clinic/practitioner for monthly compensation update.");
          }

          if (row.id != null) {
            await inlineMonthlyUpdateMut.mutateAsync({
              clinicCode: row.clinic,
              id: row.id,
              data: monthlyPayload,
            });
          } else {
            const createProduction =
              monthlyPayload.production !== undefined
                ? monthlyPayload.production
                : toNullableNumber(row.production);
            const createCollection =
              monthlyPayload.collection !== undefined
                ? monthlyPayload.collection
                : toNullableNumber(row.collection);

            if (createProduction === null || Number.isNaN(createProduction)) {
              throw new Error(
                "Production is required when creating a new monthly compensation row.",
              );
            }
            if (createCollection === null || Number.isNaN(createCollection)) {
              throw new Error(
                "Collection is required when creating a new monthly compensation row.",
              );
            }

            const createPayload = {
              practitioner: row.practitioner_id,
              month: row.month || month,
              production: createProduction,
              collection: createCollection,
            };

            INLINE_EDITABLE_MONTHLY_FIELDS.forEach((field) => {
              if (field === "production" || field === "collection") return;
              if (monthlyPayload[field] !== undefined) {
                createPayload[field] = monthlyPayload[field];
              }
            });

            await createMonthlyMut.mutateAsync({
              clinicCode: row.clinic,
              data: createPayload,
            });
          }
        }

        await queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
        showSuccess("Row saved.");
        inlineEditStore.completeSave(rowKey);
      } catch (error) {
        showError(getApiErrorMessage(error));
        inlineEditStore.stopSaving();
      }
    },
    [
      createMonthlyMut,
      getInlineRowKey,
      inlineEditStore,
      inlineMonthlyUpdateMut,
      month,
      queryClient,
      updateMut,
    ],
  );

  const handleDownloadPdf = useCallback(
    async (row) => {
      setPdfBusy({ id: row.id, action: "download" });
      try {
        await downloadPdfMut.mutateAsync({
          id: row.id,
          practitionerName: row.practitioner_name,
          month: row.month,
        });
      } catch (err) {
        showError(getApiErrorMessage(err));
      } finally {
        setPdfBusy(null);
      }
    },
    [downloadPdfMut],
  );

  const handleDownloadLabInvoices = useCallback(
    async (row) => {
      setPdfBusy({ id: row.id, action: "labInvoices" });
      try {
        await downloadLabInvoiceZipMut.mutateAsync({
          clinic: row.clinic,
          practitionerId: row.practitioner_id,
          practitionerName: row.practitioner_name,
          month: row.month,
        });
      } catch (err) {
        showError(
          getApiErrorMessage(err, "No lab invoice PDFs found for this practitioner/month."),
        );
      } finally {
        setPdfBusy(null);
      }
    },
    [downloadLabInvoiceZipMut],
  );

  // Exports every row that survives the current filters + sort (all pages),
  // not just the visible page.
  const handleDownloadExcel = useCallback(() => {
    const exportRows = buildAssociateFeeExportRows(sortedRows);
    if (!exportRows.length) {
      showError("No rows to export for the current filters.");
      return;
    }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "DDS Associate Fee");
    const label = selectedMonths.join("_") || "all";
    XLSX.writeFile(wb, `dds_associate_fee_${label}.xlsx`);
    showSuccess(`Exported ${exportRows.length} rows.`);
  }, [sortedRows, selectedMonths]);

  const handlePreviewPdf = useCallback(
    async (row) => {
      setPdfBusy({ id: row.id, action: "preview" });
      try {
        const url = await previewPdfMut.mutateAsync({ id: row.id });
        setPreviewPdf({
          url,
          title: `${row.practitioner_name} — ${row.month}`,
        });
      } catch (err) {
        showError(getApiErrorMessage(err));
      } finally {
        setPdfBusy(null);
      }
    },
    [previewPdfMut],
  );

  const closePdfPreview = useCallback(() => {
    if (previewPdf?.url) window.URL.revokeObjectURL(previewPdf.url);
    setPreviewPdf(null);
  }, [previewPdf]);

  const handleSendEmail = useCallback((row) => {
    setEmailConfirmRow(row);
  }, []);

  // ── Approval (multi-step: Area Manager → Payroll → Admin) ──
  // Each step is patched independently. The user's role + email is
  // recorded on the matching `<level>_approval` JSON field.
  // Strict role gating: only the user whose role matches the step may
  // click that step's button. Admin/organizer accounts can ONLY click
  // the Admin step (they cannot approve on behalf of Area Manager or
  // Payroll).
  const canApproveStep = useCallback(
    (step /* , row */) => {
      if (!user) return false;
      if (step.role === "area_manager") return isAreaManager(user);
      if (step.role === "payroll") return isPayroll(user);
      if (step.role === "admin") return isOrganizerOrAdmin(user);
      return false;
    },
    [user],
  );

  // Clicking a pill opens the confirm modal; the actual PATCH happens in confirmApproveStep.
  const handleApproveStep = useCallback(
    (row, step) => {
      if (!row?.id) {
        showError("Cannot approve: no monthly compensation record exists for this row yet.");
        return;
      }
      if (!canApproveStep(step, row)) {
        showError(`You don't have permission to set ${step.label} approval.`);
        return;
      }
      setApprovalConfirm({ row, step });
    },
    [canApproveStep],
  );

  const confirmApproveStep = useCallback(async () => {
    if (!approvalConfirm) return;
    const { row, step } = approvalConfirm;
    try {
      await approveMut.mutateAsync({
        clinicCode: row.clinic,
        id: row.id,
        data: {
          [step.field]: {
            approved: true,
            user: user?.email || null,
          },
        },
      });
      showSuccess(`${step.label} approved for ${row.practitioner_name}`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setApprovalConfirm(null);
    }
  }, [approvalConfirm, approveMut, user?.email]);

  // ── Approval reset workflow ──
  // Admin can click ANY green pill (AM, Payroll, Admin) → immediate wipe of
  // all 3 levels. AM / Payroll can click ONLY their own green pill, which
  // submits a pending reset request that admin then approves or rejects.
  const canResetStep = useCallback(
    (step /* , row */) => {
      if (!user) return false;
      if (isOrganizerOrAdmin(user)) return true;
      if (step.role === "area_manager") return isAreaManager(user);
      if (step.role === "payroll") return isPayroll(user);
      return false;
    },
    [user],
  );

  const handleRequestReset = useCallback(
    (row, step) => {
      if (!row?.id) return;
      if (!canResetStep(step, row)) {
        showError(`You don't have permission to reset ${step.label} approval.`);
        return;
      }
      setResetRequestConfirm({ row, step });
    },
    [canResetStep],
  );

  const confirmRequestReset = useCallback(async () => {
    if (!resetRequestConfirm) return;
    const { row } = resetRequestConfirm;
    const adminImmediate = isAdminOrOrg;
    try {
      if (adminImmediate) {
        await approveMut.mutateAsync({
          clinicCode: row.clinic,
          id: row.id,
          data: {
            am_approval: {},
            payroll_approval: {},
            admin_approval: {},
            request_reset_approval: {},
          },
        });
        showSuccess(`All approvals reset for ${row.practitioner_name}`);
      } else {
        await approveMut.mutateAsync({
          clinicCode: row.clinic,
          id: row.id,
          data: {
            request_reset_approval: {
              request: true,
              user_request: user?.email || null,
            },
          },
        });
        showSuccess(
          `Reset request submitted for ${row.practitioner_name} — awaiting admin review.`,
        );
      }
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setResetRequestConfirm(null);
    }
  }, [resetRequestConfirm, approveMut, isAdminOrOrg, user?.email]);

  const handleResolveReset = useCallback(
    (row, decision) => {
      if (!row?.id) return;
      if (!isAdminOrOrg) {
        showError("Only an admin can resolve a reset request.");
        return;
      }
      setResetResolveConfirm({ row, decision });
    },
    [isAdminOrOrg],
  );

  const confirmResolveReset = useCallback(async () => {
    if (!resetResolveConfirm) return;
    const { row, decision } = resetResolveConfirm;
    try {
      if (decision === "approve") {
        await approveMut.mutateAsync({
          clinicCode: row.clinic,
          id: row.id,
          data: {
            am_approval: {},
            payroll_approval: {},
            admin_approval: {},
            request_reset_approval: {},
          },
        });
        showSuccess(`Reset approved — all approvals cleared for ${row.practitioner_name}.`);
      } else {
        await approveMut.mutateAsync({
          clinicCode: row.clinic,
          id: row.id,
          data: { request_reset_approval: {} },
        });
        showSuccess(`Reset request rejected for ${row.practitioner_name}.`);
      }
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setResetResolveConfirm(null);
    }
  }, [resetResolveConfirm, approveMut]);

  // ── Bulk approval (final / admin level) ──
  const unapprovedRows = useMemo(
    () =>
      pagedRows.filter(
        (r) => !r._isSyntheticMissingMonthlyRecon && !isApprovalApproved(r.admin_approval),
      ),
    [pagedRows],
  );

  const toggleSelectRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allUnapprovedIds = unapprovedRows.map((r) => r.id);
      const allSelected =
        allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allUnapprovedIds);
    });
  }, [unapprovedRows]);

  // Clear selections when data changes.
  // NOTE: we intentionally react to the DEFERRED filter values so these
  // effects do not pile extra urgent renders onto every checkbox click.
  // We also bail out via a functional updater when the Set is already
  // empty — otherwise React schedules a re-render with a fresh (but
  // semantically identical) Set reference, which forces the whole table
  // to re-render after every filter/sort change.  See profile commit #2.
  useEffect(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [
    month,
    clinicFilter,
    selectedClinicCodes,
    page,
    pageSize,
    sortKey,
    sortDir,
    deferredColumnFilters,
    deferredDdsCompletionFilter,
  ]);

  useEffect(() => {
    inlineEditStore.clear();
  }, [
    clinicFilter,
    columnFilters,
    ddsCompletionFilter,
    inlineEditStore,
    month,
    page,
    pageSize,
    selectedClinicCodes,
    sortDir,
    sortKey,
  ]);

  useEffect(() => {
    compareModalRef.current?.close();
    practitionerModalRef.current?.close();
  }, [month, clinicFilter, selectedClinicCodes]);

  // Reset to page 1 when sort / filter changes — but ONLY if we are not
  // already on page 1.  Otherwise we trigger an extra parent state
  // update and a full table re-render for nothing.  See profile commit #3.
  //
  // NOTE: `page` must be read via a ref instead of being a dep — otherwise
  // every page navigation (1 → 2) re-fires this effect and snaps the user
  // straight back to page 1.
  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    if (pageRef.current !== 1) onPageChange?.(1);
  }, [sortKey, sortDir, deferredColumnFilters, deferredDdsCompletionFilter, onPageChange]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) onPageChange?.(maxPage);
  }, [page, pageSize, total, onPageChange]);

  const selectedUnapproved = useMemo(
    () =>
      pagedRows.filter(
        (r) =>
          !r._isSyntheticMissingMonthlyRecon &&
          !isApprovalApproved(r.admin_approval) &&
          selectedIds.has(r.id),
      ),
    [pagedRows, selectedIds],
  );

  const handleBulkApproveClick = useCallback(() => {
    if (selectedUnapproved.length === 0) {
      showError("No unapproved rows selected.");
      return;
    }
    setBulkApproveOpen(true);
  }, [selectedUnapproved]);

  const [bulkApproving, setBulkApproving] = useState(false);

  const confirmBulkApprove = useCallback(async () => {
    setBulkApproving(true);
    try {
      // Bulk = final Admin-level approval. PATCH `admin_approval` on
      // each selected record so the per-row stepper reflects the
      // current admin user. We do not touch the legacy `approval` flag.
      const adminPayload = { approved: true, user: user?.email || null };
      await Promise.all(
        selectedUnapproved.map((r) =>
          approveMut.mutateAsync({
            clinicCode: r.clinic,
            id: r.id,
            data: { admin_approval: adminPayload },
          }),
        ),
      );
      showSuccess(`All ${selectedUnapproved.length} records approved!`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setBulkApproving(false);
      setBulkApproveOpen(false);
      setSelectedIds(new Set());
    }
  }, [selectedUnapproved, approveMut, user?.email]);

  const confirmSendEmail = useCallback(async () => {
    if (!emailConfirmRow) return;
    try {
      const result = await sendEmailMut.mutateAsync(emailConfirmRow.id);
      showSuccess(result.message || `PDF emailed to ${emailConfirmRow.practitioner_email}`);
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setEmailConfirmRow(null);
    }
  }, [emailConfirmRow, sendEmailMut, queryClient]);

  // ── Bulk email ──
  const handleBulkEmailClick = useCallback(async () => {
    try {
      // Fan out one request per selected month; backend `_build_associate_fee_rows`
      // is single-month only, so we collect rows per month and concat.
      const monthsToFetch = selectedMonths.length ? selectedMonths : [month].filter(Boolean);
      if (!monthsToFetch.length) {
        showError("Please select at least one month.");
        return;
      }

      const fetchForMonth = async (m) => {
        if (isAdminOrOrg) {
          const url = buildUrlWithParams(
            adminEndpoints.GET_ALL_MONTHLY_COMPENSATIONS,
            {
              month: m,
              practitioner_type: "dds",
              ...(clinicFilter ? { clinic: clinicFilter } : {}),
              limit: 10000,
              offset: 0,
            },
            { clean: true },
          );
          const res = await api.get(url);
          return res.data?.results ?? [];
        }
        if (clinicFilter) {
          // clinicFilter may be comma-separated; the per-clinic endpoint
          // takes a single code, so fan out for each.
          const codes = clinicFilter
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
          const responses = await Promise.all(
            codes.map((code) => {
              const url = buildUrlWithParams(
                clinicEndpoints.GET_MONTHLY_COMPENSATION_BY_CLINIC(code),
                { month: m, practitioner_type: "dds", limit: 10000, offset: 0 },
                { clean: true },
              );
              return api.get(url);
            }),
          );
          return responses.flatMap((res) => res.data?.results ?? []);
        }
        // All clinics for Role 3 — fetch each assigned clinic
        const responses = await Promise.all(
          userClinics.map((code) => {
            const url = buildUrlWithParams(
              clinicEndpoints.GET_MONTHLY_COMPENSATION_BY_CLINIC(code),
              { month: m, practitioner_type: "dds", limit: 10000, offset: 0 },
              { clean: true },
            );
            return api.get(url);
          }),
        );
        return responses.flatMap((res) => res.data?.results ?? []);
      };

      const perMonth = await Promise.all(monthsToFetch.map((m) => fetchForMonth(m)));
      let all = perMonth.flat();
      if (hasSelectedClinicCodes) {
        all = all.filter((row) =>
          selectedClinicCodeSet.has(String(row.clinic || "").toUpperCase()),
        );
      }
      const withEmail = all.filter((r) => r.practitioner_email);
      const noEmail = all.filter((r) => !r.practitioner_email);
      // Separate approved vs not approved
      const notApproved = withEmail.filter((r) => !isApprovalApproved(r.admin_approval));
      const approved = withEmail.filter((r) => isApprovalApproved(r.admin_approval));
      // Already-sent rows are still eligible — sending again counts as a resend
      // and the backend will refresh the email_sent_at timestamp.
      const alreadySent = approved.filter((r) => r.email_sent_at);
      const eligible = approved;
      if (withEmail.length === 0 && noEmail.length === 0) {
        showError("No practitioners found.");
        return;
      }
      if (withEmail.length === 0) {
        showError(
          "All practitioners are missing email addresses. Please update their profiles first.",
        );
        return;
      }
      if (eligible.length === 0) {
        showError("No approved practitioners available to email. Please approve records first.");
        return;
      }
      setBulkEligible(eligible);
      setBulkAlreadySent(alreadySent);
      setBulkMissingEmail(noEmail);
      setBulkNotApproved(notApproved);
      setBulkEmailOpen(true);
    } catch {
      showError("Failed to load practitioners.");
    }
  }, [
    selectedMonths,
    month,
    clinicFilter,
    isAdminOrOrg,
    userClinics,
    hasSelectedClinicCodes,
    selectedClinicCodeSet,
  ]);

  const confirmBulkEmail = useCallback(async () => {
    setBulkSending(true);
    try {
      const ids = bulkEligible.map((r) => r.id);
      const result = await bulkEmailMut.mutateAsync(ids);
      setBulkJobId(result.job_id);
    } catch (err) {
      setBulkSending(false);
      if (err.response?.status === 409) {
        showError("A bulk email job is already in progress for this month/clinic.");
      } else {
        setBulkEmailOpen(false);
        showError("Bulk email request failed. Please try again.");
      }
    }
  }, [bulkEligible, bulkEmailMut]);

  const cancelBulkEmail = useCallback(() => {
    if (!bulkSending) setBulkEmailOpen(false);
  }, [bulkSending]);

  const getRowKey = useCallback(
    (row, index) => row.id ?? `${row.clinic}-${row.practitioner_id ?? index}`,
    [],
  );

  // Row hover highlight.
  //
  // A plain `hover:bg-*` on the row is not enough here: most columns in this
  // table set their OWN cell background (see utils/columnColors.js — sky for
  // production/collection, violet for deductions, green for DDS draw, ...), and
  // those paint over anything the row underneath sets. So this does both:
  //
  //   - `hover:bg-*` on the row  -> shows through the cells that ARE transparent
  //     (select checkbox, clinic, practitioner name).
  //   - `group` + `group-hover:brightness-95` on the cells (see cellClassName on
  //     the table below) -> darkens each coloured cell against its own
  //     background, so the whole row reads as highlighted while the columns keep
  //     their colour coding.
  const getAssociateFeeRowClassName = useCallback(
    () => "group hover:bg-palette-indigo-soft transition-colors",
    [],
  );

  // DataTable's own default plus the group-hover dimming described above.
  // Repeating the default is deliberate — passing `cellClassName` replaces it.
  const associateFeeCellClassName =
    "font-semibold text-palette-slate-primary transition group-hover:brightness-95";

  const allColumns = useMemo(
    () =>
      payrollColumns(canEditPractitioner ? handleUpdateClick : null, {
        onOpenPdf: canDownloadPdf ? handleDownloadPdf : null,
        onDownloadLabInvoices: canDownloadPdf ? handleDownloadLabInvoices : null,
        onPreviewPdf: canDownloadPdf ? handlePreviewPdf : null,
        onSendEmail: canSendEmail ? handleSendEmail : null,
        onApproveStep: canApprove ? handleApproveStep : null,
        canApproveStep: canApprove ? canApproveStep : null,
        onRequestReset: canApprove ? handleRequestReset : null,
        canResetStep: canApprove ? canResetStep : null,
        onResolveReset: isAdminOrOrg ? handleResolveReset : null,
        canResolveReset: isAdminOrOrg,
        onCompareHistory: handleCompareHistory,
        onPractitionerClick: handlePractitionerNameClick,
        previousMonthDraws,
        allRows: rows,
        deductionsExpanded,
        onToggleDeductions: toggleDeductions,
        pdfBusy,
      }),
    [
      canEditPractitioner,
      canApprove,
      canSendEmail,
      canDownloadPdf,
      handleUpdateClick,
      handleDownloadPdf,
      handleDownloadLabInvoices,
      handlePreviewPdf,
      handleSendEmail,
      handleApproveStep,
      canApproveStep,
      handleRequestReset,
      canResetStep,
      handleResolveReset,
      isAdminOrOrg,
      handleCompareHistory,
      handlePractitionerNameClick,
      previousMonthDraws,
      rows,
      deductionsExpanded,
      toggleDeductions,
      pdfBusy,
    ],
  );

  const columns = useMemo(
    () =>
      allColumns.filter((c) => {
        if (hiddenColumns.has(c.key)) return false;
        if (!deductionsExpanded && DEDUCTION_COLUMN_KEYS.includes(c.key)) return false;
        return true;
      }),
    [allColumns, hiddenColumns, deductionsExpanded],
  );

  const columnsWithInlineEdit = useMemo(() => {
    const editableColumnKeys = new Set(INLINE_EDITABLE_FIELDS);

    const nextColumns = columns.map((column) => {
      if (!editableColumnKeys.has(column.key)) return column;

      const renderStaticCell = (row, rowIndex) => {
        if (column.key === "associate_fee_percentage") {
          const value = toNullableNumber(row.associate_fee_percentage);
          if (value === null || Number.isNaN(value)) {
            return <span className="text-palette-gray-placeholder text-xs italic">Set fee %</span>;
          }
          return `${value.toFixed(2)}%`;
        }
        return column.render ? column.render(row, rowIndex) : row?.[column.key];
      };

      return {
        ...column,
        render: (row, rowIndex) => {
          const rowKey = getInlineRowKey(row);
          return (
            <InlineEditableCell
              row={row}
              rowIndex={rowIndex}
              rowKey={rowKey}
              columnKey={column.key}
              store={inlineEditStore}
              onDraftChange={updateInlineDraft}
              renderStaticCell={renderStaticCell}
            />
          );
        },
      };
    });

    const actionColumn = {
      key: INLINE_ACTION_COLUMN_KEY,
      header: "EDIT",
      width: 50,
      cellClassName: "whitespace-nowrap",
      render: (row) => {
        const rowKey = getInlineRowKey(row);
        return (
          <InlineEditActionCell
            row={row}
            rowKey={rowKey}
            store={inlineEditStore}
            canEditPractitioner={canEditPractitioner}
            isAdminOrOrg={isAdminOrOrg}
            onStartEdit={startInlineEdit}
            onSave={saveInlineEdit}
            onCancel={cancelInlineEdit}
          />
        );
      },
    };

    return [actionColumn, ...nextColumns];
  }, [
    canEditPractitioner,
    cancelInlineEdit,
    columns,
    getInlineRowKey,
    inlineEditStore,
    isAdminOrOrg,
    saveInlineEdit,
    startInlineEdit,
    updateInlineDraft,
  ]);

  const setColumnFilter = useCallback(
    (key, nextValue) => {
      // 1. URGENT: flip the store so ONLY the affected header menu
      //    re-renders (via useFilterValue).  Parent stays untouched,
      //    columnsWithMenus does NOT rebuild.
      menuStore.setFilter(key, nextValue ?? null);

      // 2. DEFERRED: update the data-driving React state in a
      //    transition on the next frame, so the heavy row-filter
      //    recompute does not block the checkbox-flip paint.
      requestAnimationFrame(() => {
        startFilterTransition(() => {
          setColumnFilters((prev) => {
            const next = { ...prev };
            if (!nextValue) delete next[key];
            else next[key] = nextValue;
            return next;
          });
        });
      });
    },
    [menuStore, setColumnFilters, startFilterTransition],
  );

  const setColumnSort = useCallback(
    (key, direction) => {
      if (!direction) {
        if (menuStore.getSortKey() !== key) return;
        menuStore.setSort(null, null);
      } else {
        menuStore.setSort(key, direction);
      }
      requestAnimationFrame(() => {
        startSortTransition(() => {
          setSortKey(direction ? key : null);
          setSortDir(direction ?? null);
        });
      });
    },
    [menuStore, setSortDir, setSortKey, startSortTransition],
  );

  const handleDdsCompletionFilterChange = useCallback(
    (nextValue) => {
      if (ddsCompletionFilter === nextValue) return;
      requestAnimationFrame(() => {
        startFilterTransition(() => {
          setDdsCompletionFilter(nextValue);
        });
      });
    },
    [ddsCompletionFilter, setDdsCompletionFilter, startFilterTransition],
  );

  const clearAllFilters = useCallback(() => {
    menuStore.setOpenKey(null);
    menuStore.clearFilters();
    completionFilterBarRef.current?.reset();
    inlineEditStore.clear();
    requestAnimationFrame(() => {
      startFilterTransition(() => {
        setColumnFilters({});
        setDdsCompletionFilter("all");
        onPageChange?.(1);
      });
    });
  }, [
    inlineEditStore,
    menuStore,
    onPageChange,
    setColumnFilters,
    setDdsCompletionFilter,
    startFilterTransition,
  ]);

  // NOTE: `columnFilters` is deliberately NOT in this memo's deps.
  //   The per-column "selected labels" now live in the external store
  //   and AssociateFeeHeaderMenu reads them via useFilterValue().  A
  //   filter-checkbox click therefore does NOT rebuild all 30 header
  //   JSX elements — only the affected menu re-renders.
  const columnsWithMenus = useMemo(
    () =>
      columnsWithInlineEdit.map((column) => {
        if (
          ["_select", "pdf", "email", "approval", "total_fee", INLINE_ACTION_COLUMN_KEY].includes(
            column.key,
          )
        )
          return column;

        return {
          ...column,
          header: (
            <AssociateFeeHeaderMenu
              column={column}
              rows={rows}
              onSortChange={setColumnSort}
              onFilterChange={setColumnFilter}
              menuStore={menuStore}
              totalDisplay={formatHeaderTotal(column.key, columnTotals[column.key])}
              totalBadgeClassName={TOTAL_BADGE_COLORS[column.key]}
            />
          ),
        };
      }),
    [columnsWithInlineEdit, rows, setColumnSort, setColumnFilter, menuStore, columnTotals],
  );

  const toggleColumn = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem("associateFee_hiddenColumns", JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Prepend selection checkbox column
  const columnsWithSelect = useMemo(() => {
    const allUnapprovedIds = unapprovedRows.map((r) => r.id);
    const allSelected =
      allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => selectedIds.has(id));
    return [
      {
        key: "_select",
        header: (
          <CheckboxField
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={allUnapprovedIds.length === 0}
          />
        ),
        width: 40,
        render: (r) =>
          isApprovalApproved(r.admin_approval) || r._isSyntheticMissingMonthlyRecon ? null : (
            <CheckboxField checked={selectedIds.has(r.id)} onChange={() => toggleSelectRow(r.id)} />
          ),
      },
      ...columnsWithMenus,
    ];
  }, [columnsWithMenus, unapprovedRows, selectedIds, toggleSelectAll, toggleSelectRow]);

  return (
    <div className="w-full overflow-x-hidden">
      {bulkApproveOpen && (
        <ConfirmModal
          isOpen={bulkApproveOpen}
          onClose={() => !bulkApproving && setBulkApproveOpen(false)}
          onConfirm={confirmBulkApprove}
          title="Approve Selected Records"
          message={
            <div>
              <p className="text-sm mb-2">
                Approve <strong>{selectedUnapproved.length}</strong> record
                {selectedUnapproved.length !== 1 ? "s" : ""}?
              </p>
              <div className="max-h-48 overflow-y-auto rounded border border-palette-gray-divider bg-palette-gray-soft p-2">
                {selectedUnapproved.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-1 text-sm">
                    <span className="font-medium">{r.practitioner_name}</span>
                    <span className="text-palette-gray-secondary">{r.clinic}</span>
                  </div>
                ))}
              </div>
            </div>
          }
          confirmText={bulkApproving ? `Approving...` : `Approve ${selectedUnapproved.length}`}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={bulkApproving}
        />
      )}
      {emailConfirmRow && (
        <ConfirmModal
          isOpen={!!emailConfirmRow}
          // Building the PDF takes a few seconds now — matching the approval
          // modals, dismissing mid-send is blocked so the row can't be closed
          // out from under an in-flight request.
          onClose={() => (sendEmailMut.isPending ? null : setEmailConfirmRow(null))}
          onConfirm={confirmSendEmail}
          title={emailConfirmRow.email_sent_at ? "Resend Email" : "Send Email"}
          message={
            emailConfirmRow.email_sent_at
              ? `Resend associate fee PDF to ${emailConfirmRow.practitioner_name} (${emailConfirmRow.practitioner_email})? Last sent ${new Date(emailConfirmRow.email_sent_at).toLocaleString()}.`
              : `Send associate fee PDF to ${emailConfirmRow.practitioner_name} (${emailConfirmRow.practitioner_email})?`
          }
          confirmText={
            sendEmailMut.isPending
              ? "Sending..."
              : emailConfirmRow.email_sent_at
                ? "Resend"
                : "Send"
          }
          cancelText="Cancel"
          isLoading={sendEmailMut.isPending}
        />
      )}
      {approvalConfirm && (
        <ConfirmModal
          isOpen={!!approvalConfirm}
          onClose={() => (approveMut.isPending ? null : setApprovalConfirm(null))}
          onConfirm={confirmApproveStep}
          title={`Confirm ${approvalConfirm.step.label} Approval`}
          message={`Approve ${approvalConfirm.step.label} for ${approvalConfirm.row.practitioner_name} (${approvalConfirm.row.clinic}, ${approvalConfirm.row.month})? This will be recorded under your email (${user?.email || "unknown"}).`}
          confirmText={approveMut.isPending ? "Approving..." : "Approve"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={approveMut.isPending}
        />
      )}
      {resetRequestConfirm && (
        <ConfirmModal
          isOpen={!!resetRequestConfirm}
          onClose={() => (approveMut.isPending ? null : setResetRequestConfirm(null))}
          onConfirm={confirmRequestReset}
          title={
            resetRequestConfirm.step.role === "admin" && isAdminOrOrg
              ? "Reset All Approvals"
              : isAdminOrOrg
                ? `Reset All Approvals (via ${resetRequestConfirm.step.label})`
                : `Request ${resetRequestConfirm.step.label} Reset`
          }
          message={
            isAdminOrOrg
              ? `This will clear ALL three approval levels for ${resetRequestConfirm.row.practitioner_name} (${resetRequestConfirm.row.clinic}, ${resetRequestConfirm.row.month}). The approval process will start from scratch. Continue?`
              : `Submit a reset request for ${resetRequestConfirm.row.practitioner_name} (${resetRequestConfirm.row.clinic}, ${resetRequestConfirm.row.month})? An admin must approve before the approvals are cleared.`
          }
          confirmText={
            approveMut.isPending ? "Submitting..." : isAdminOrOrg ? "Reset All" : "Request Reset"
          }
          confirmVariant={isAdminOrOrg ? "danger" : "primary"}
          cancelText="Cancel"
          isLoading={approveMut.isPending}
        />
      )}
      {resetResolveConfirm && (
        <ConfirmModal
          isOpen={!!resetResolveConfirm}
          onClose={() => (approveMut.isPending ? null : setResetResolveConfirm(null))}
          onConfirm={confirmResolveReset}
          title={
            resetResolveConfirm.decision === "approve"
              ? "Approve Reset Request"
              : "Reject Reset Request"
          }
          message={
            resetResolveConfirm.decision === "approve"
              ? `Approve the reset request for ${resetResolveConfirm.row.practitioner_name}? This will clear ALL three approval levels.`
              : `Reject the reset request for ${resetResolveConfirm.row.practitioner_name}? Existing approvals will remain unchanged.`
          }
          confirmText={
            approveMut.isPending
              ? "Working..."
              : resetResolveConfirm.decision === "approve"
                ? "Approve Reset"
                : "Reject Reset"
          }
          confirmVariant={resetResolveConfirm.decision === "approve" ? "danger" : "primary"}
          cancelText="Cancel"
          isLoading={approveMut.isPending}
        />
      )}
      {bulkEmailOpen && (
        <ConfirmModal
          isOpen={bulkEmailOpen}
          onClose={cancelBulkEmail}
          onConfirm={confirmBulkEmail}
          title="Send All Emails"
          size="lg"
          confirmText={
            bulkSending
              ? `Sending${bulkJobStatus ? ` (${bulkJobStatus.sent || 0}/${bulkJobStatus.total || bulkEligible.length})` : "..."}`
              : `Send ${bulkEligible.length} Emails`
          }
          cancelText="Cancel"
          isLoading={bulkSending}
          message={
            <BulkEmailSummary
              alreadySent={bulkAlreadySent}
              missingEmail={bulkMissingEmail}
              notApproved={bulkNotApproved}
              eligible={bulkEligible}
            />
          }
        />
      )}
      <PractitionerModalWrapper ref={practitionerModalRef} onSave={handleSavePractitioner} />
      <DdsComparisonModal ref={compareModalRef} />
      <Modal
        isOpen={!!previewPdf}
        onClose={closePdfPreview}
        title={previewPdf?.title || "PDF Preview"}
        size="3xl"
      >
        {previewPdf?.url && (
          <iframe
            src={previewPdf.url}
            title="PDF Preview"
            className="w-full rounded"
            // Fill the panel instead of a fixed 70vh. Modal caps its panel at
            // 90vh, and the header + content padding take ~104px of that, so
            // this is the tallest the iframe can be without pushing the panel
            // past its cap and producing a second, nested scrollbar.
            style={{ height: "calc(90vh - 104px)", border: "none" }}
          />
        )}
      </Modal>
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">DDS Associate Fee</h3>
            <ColumnToggle
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              onChange={toggleColumn}
            />
            <DdsCompletionFilterBar
              ref={completionFilterBarRef}
              options={DDS_COMPLETION_FILTER_OPTIONS}
              counts={completionCounts}
              onChange={handleDdsCompletionFilterChange}
              initialValue={ddsCompletionFilter}
            />
            {activeFilterCount > 0 && (
              <Button
                variant="gray"
                size="sm"
                onClick={clearAllFilters}
                title="Clear all column filters"
              >
                {`Clear Applied Filters (${activeFilterCount})`}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedUnapproved.length > 0 && isAdminOrOrg && (
              <Button variant="outline" size="sm" onClick={handleBulkApproveClick}>
                Approve Selected ({selectedUnapproved.length})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadExcel}
              disabled={isLoading || sortedRows.length === 0}
              title="Download the filtered rows as an Excel file"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                />
              </svg>
              Download Excel
            </Button>
            {canSendEmail && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkEmailClick}
                disabled={isLoading || rows.length === 0}
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Send All Emails
              </Button>
            )}
          </div>
        </div>

        <Card bodyClassName="p-0">
          <DataStateHandler
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && rows.length === 0}
            loadingMessage="Loading associate fees..."
            emptyMessage={
              clinicFilter
                ? "No data for this month"
                : "Select one or more clinics to view associate fees."
            }
            showLoadingSpinner={true}
          >
            <StickyHorizontalScroll
              className={`max-w-full transition-opacity ${isSorting || isFiltering || isFilterSettling ? "opacity-60" : ""}`}
            >
              <div className="min-w-max">
                <PaginatedTable
                  mode="server"
                  columns={columnsWithSelect}
                  data={pagedRows}
                  rowClassName={getAssociateFeeRowClassName}
                  cellClassName={associateFeeCellClassName}
                  emptyMessage="No DDS associate fee records match the current filters."
                  rowKey={getRowKey}
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  wrapperClassName="pt-6"
                />
              </div>
            </StickyHorizontalScroll>
          </DataStateHandler>
        </Card>
      </Card>
    </div>
  );
}
